import React from "react";
import "./confirmation.css";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Data {
  companyName: string;
  idNumber: string;
  requestId: string;
  taxNumber: string;
  address: string;
  place: string;
  responsiblePersonFullName: string;
  responsiblePersonFunction: string;
  companyEmailAddress: string;
  companyPhoneNumber: string;
  contactPersonFirstName: string;
  contactPersonLastName: string;
  contactPersonEmail: string;
  languageId: string;
}

const ConfirmationPage = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const titleMapping: { [key: string]: string } = {
    requestId: t("request:request-id", "Request ID"),
    contactPersonFirstName: t("register:first-name", "First Name"),
    contactPersonLastName: t("register:last-name", "Last Name"),
    contactPersonEmail: t("register:email-address", "Email address"),
    companyPhoneNumber: t("global.phone-number", "Phone number"),
    receiveNews: t("register:i-would-like-to-receive-news", "I would like to receive news"),
    hasVatIdAndAcceptsTerms: t(
      "register:i-have-vat-id-and-accept-terms-conditions",
      "I have VAT ID and accept Terms & Conditions"
    ),
    companyName: t("requests:create.company-name", "Company Name"),
    companyEmailAddress: t("request:create.primary-email", "Company Primary E-mail"),
    taxNumber: t("requests:create.tax-number", "Tax Number"),
    countryId: t("register:country", "Country"),
    address: t("global.address", "Address"),
    place: t("register:city", "City"),
    postCode: t("register:postal-code", "Postal Code"),
    smtNumber: t("request:create.smt-number", "SMT Number"),
    cipCidNumber: t("request:create.cip-cid-number", "CIP/CID Number"),
    aconNumber: t("request:create.acon-number", "ACON No."),
    vatNumber: t("request:create.vat-number", "VAT Number"),
    roadLog: t("request:create.road-log", "Road LOG"),
    vehicalFleet: t("request:total-vehicle-fleet", "Total Number of Fleet Vehicles"),
    trailers: t("request:trailer-type", "Trailers By Type"),
    specialLicence: t("request:special-license", "Special License"),
    specialLicense: t("request:special-license", "Special License"),
    cemtPermits: t("request:cemt-permits", "CEMT Permits"),
    certificate: t("request:certificate", "Certificate"),
    certificates: t("request:certificate", "Certificate"),
    licences: t("request:licences", "Licences"),
    licenses: t("request:licences", "Licences"),
    operatingCountries: t("request:operating-countries", "Operating Country"),
    operatingCountry: t("request:operating-countries", "Operating Country"),
    trailerType: t("request:trailer-type-header", "Trailer Type"),
    trailerQty: t("request:trailer-qty", "Trailer Qty"),
    cemtPermit: t("request:cemt-permit", "CEMT Permit"),
    country: t("register:country", "Country"),
    // Question 4: Vehicle Types
    vehicles: t("request:vehicle-types", "Vehicle Types"),
    vehicleType: t("request:vehicle-type", "Vehicle Type"),
    "Vehicle QTY": t("request:vehicle-qty", "Vehicle QTY"),
    // Question 5: Body Types
    body: t("request:body-types", "Body Types"),
    bodyType: t("request:body-type", "Body Type"),
    "Body QTY": t("request:body-qty", "Body QTY"),
    typeOfTransport:t("request:type-of-transport", "Type Of Transport")
  };

  const data = location.state as Data;
  const filteredData = { ...data };

  const arraySectionKeys = [
    "trailers",
    "vehicles",        // Question 4: Vehicle Types
    "body",            // Question 5: Body Types
    "cemtPermits",
    "specialLicence",
    "specialLicense",
    "certificate",
    "certificates",
    "licences",
    "licenses",
    "operatingCountries",
    "operatingCountry",
    "typeOfTransport"
  ];

  // Define the order for regular fields
  const fieldOrder = [
    "requestId",
    "contactPersonFirstName",
    "contactPersonLastName",
    "contactPersonEmail",
    "companyPhoneNumber",
    "receiveNews",
    "hasVatIdAndAcceptsTerms",
    "companyName",
    "companyEmailAddress",
    "taxNumber",
    "smtNumber",
    "cipCidNumber",
    "aconNumber",
    "vatNumber",
    "roadLog",
    "countryId",
    "address",
    "place",
    "postCode",
    "vehicalFleet",
  ];

  const printDocument = () => {
    window.print();
  };
  const isArraySection = (key: string) => arraySectionKeys.includes(key);

  const renderNestedTable = (key: string, value: any) => {
    // Handle null/undefined or empty string
    if (value === null || value === undefined || value === "" || value === "-") {
      return (
        <table className="inner-table">
          <thead>
            <tr>
              <th>{titleMapping[key] || key}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>-</td>
            </tr>
          </tbody>
        </table>
      );
    }

    // Handle single string value - convert to single row table
    if (typeof value === "string") {
      return (
        <table className="inner-table">
          <thead>
            <tr>
              <th>{titleMapping[key] || key}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{value}</td>
            </tr>
          </tbody>
        </table>
      );
    }

    // Handle empty array
    if (Array.isArray(value) && value.length === 0) {
      return (
        <table className="inner-table">
          <thead>
            <tr>
              <th>{titleMapping[key] || key}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>-</td>
            </tr>
          </tbody>
        </table>
      );
    }

    // Handle array of objects
    if (Array.isArray(value) && typeof value[0] === "object") {
      const headers = Object.keys(value[0]);
      return (
        <table className="inner-table">
          <thead>
            <tr>
              {headers.map((header) => (
                <th key={header}>{titleMapping[header] || header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {value.map((item: any, idx: number) => (
              <tr key={idx}>
                {Object.values(item).map((val: any, i: number) => (
                  <td key={i}>{val}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    // Handle array of strings/numbers
    if (Array.isArray(value)) {
      return (
        <table className="inner-table">
          <thead>
            <tr>
              <th>{titleMapping[key] || key}</th>
            </tr>
          </thead>
          <tbody>
            {value.map((item: any, i: number) => (
              <tr key={i}>
                <td>{item}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    // Fallback for other types
    return (
      <table className="inner-table">
        <thead>
          <tr>
            <th>{titleMapping[key] || key}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{String(value)}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderValue = (key: string, value: any) => {
    // Handle boolean fields
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    // Handle receiveNews field specifically (might come as string "true"/"false" or "-")
    if (key === "receiveNews") {
      if (value === true || value === "true" || value === "Yes") {
        return "Yes";
      }
      return "No";
    }
    return value || "-";
  };

  // Filter and sort regular entries according to fieldOrder
  const regularEntries = Object.entries(filteredData)
    .filter(([key]) => !isArraySection(key))
    .sort(([keyA], [keyB]) => {
      const indexA = fieldOrder.indexOf(keyA);
      const indexB = fieldOrder.indexOf(keyB);
      // If key not in fieldOrder, put it at the end
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  const arrayEntries = Object.entries(filteredData).filter(([key]) =>
    isArraySection(key)
  );

  return (
    <div className="confirmation-page-wrapper">
      <div className="confirmation-container">
        {/* Success Banner */}
        <div className="success-banner">
          <h1>{t("request:success", "Success!")}</h1>
        </div>

        {/* Inner Card */}
        <div className="inner-card" id="printable-section">
          {/* Title */}
          <h2 className="page-title">
            {t("request:submission-confirmation", "Submission Confirmation : Entry Received")}
          </h2>

          {/* Data Content */}
          <div className="data-content">
            {/* Regular Fields */}
            {regularEntries.map(([key, value]: any) => (
              <div className="data-row" key={key}>
                <div className="label">{titleMapping[key] || key}</div>
                <div className="value">{renderValue(key, value)}</div>
              </div>
            ))}

            {/* Array Sections */}
            {arrayEntries.map(([key, value]: any) => (
              <React.Fragment key={key}>
                <hr className="section-line" />
                <div className="array-section">
                  <div className="label">{titleMapping[key] || key}</div>
                  <div className="value">{renderNestedTable(key, value)}</div>
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Buttons */}
          <div className="button-section">
            <button className="print-btn" onClick={printDocument}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z"
                />
              </svg>
              <span>{t("request:print", "Print")}</span>
            </button>
            <Link to="/" className="home-btn">
              {t("request:go-to-homepage", "Go to home page")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;
